/**
 * §11 — Batch 2's pre-defined minutes form, in the order the spec's table lists its fields.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE DESIGN INTENT, WHICH IS THE REASON THE FORM LOOKS LIKE THIS
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * §11: *"as little manual input as possible. Three input classes — (1) carried forward from
 * the case and the letter, (2) auto-composed from structured picks, (3) genuinely manual."*
 * So class (1) is rendered read-only with its `Pre-Populated` tag and not as an empty box
 * somebody has to retype; class (2) is the decision builder, whose segmented picks recompose
 * the decision paragraph live through `composeDecisionText` — the SAME function the server
 * composes the filed minutes and the Decision Letter with, which is what §11 means by
 * *"stops the minutes and the letter drifting apart"*; and class (3) is the six fields the
 * banner names.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THREE PLACES THIS DEPARTS FROM §11, AND WHY
 * ─────────────────────────────────────────────────────────────────────────────
 *
 *  1. **`Attitude At The Hearing` is a three-point control, not a free textarea.** §11's
 *     placeholder asks for *"Not Good, Good or Very Good, with the narrative"*, and the
 *     record stores `hearing_attitude`, which is exactly those three words — §8 card 04
 *     renders it as a three-point control and both generated documents print it as one word.
 *     A textarea whose first word had to be one of three, silently, is a validation trap. The
 *     narrative it asked for is the `Reasoning` field below, which is what §12's `REASONING:`
 *     block prints.
 *  2. **`Finding` and `Reasoning` are on the form.** §11's table does not list them and §8's
 *     card 04 does, as the two full-width fields of the decision record; `PUT
 *     /hearings/:id/minutes` carries both, and §12's letter prints both. A field the letter
 *     prints and no screen captures is a blank in a served document.
 *  3. **The composed decision is a read-only view, not an editable textarea.** See
 *     {@link COMPOSED_DECISION_HINT}: the route has no field for an edited paragraph, so a
 *     textarea would take typing and discard it.
 *  4. **`Attended By` has no read-only automatic list, and the rows arrive filled in**
 *     (2026-09-24). §11 put the manager, the chair and the persons involved in a fixed list
 *     above six empty boxes, and §8 had both printed. Chairing two real hearings showed what
 *     that costs: *"when I enter the 4 people in the attendance section, people will appear
 *     twice… when I create the draft, it will always show 7-8 people by default"*. See
 *     {@link seedAttendeeRows}, and `presentBlock` in `@hockeytd/shared` for the other half.
 *
 * Everything else — the field order, the tags, the six attendee rows, the role dropdown's own
 * order (amended the same day, see {@link ATTENDEE_ROLES}), the testimony opener, the
 * signature line and its live update — is §11's.
 */

import { useMemo, useState } from "react";

import {
  HEARING_MINUTES_WORKSPACE_LABELS,
  composeTestimonyOpener,
  type HearingAttitude,
  type HearingSanction,
  type Seriousness,
} from "@hockeytd/shared";

import type { HearingCaseDetailView, HearingMinutesRequest } from "../../api/types.js";
import { useTournament } from "../../app/session.js";
import { formatDay, formatInstant } from "./caseText.js";
import { DocumentPaper, DocumentView } from "./DocumentWorkspace.js";
import {
  ATTENDEE_HINT,
  ATTENDEE_ROLES,
  ATTENDEE_ROW_COUNT,
  letterheadEventName,
  COMPOSED_DECISION_HINT,
  MINUTES_PREFILL_BANNER,
  TESTIMONY_HINT,
  WORKSPACE_PRIMARY,
  WORKSPACE_PRIMARY_CAPTION,
  asDocumentCharge,
  composeDecisionPreview,
  minutesSignatureLine,
  workspaceTitle,
  type DecisionPicks,
} from "./documentText.js";

/** One of §11's six editable rows. A row with no name is simply not an attendee. */
interface AttendeeRow {
  name: string;
  role: string;
}

const EMPTY_ROWS: readonly AttendeeRow[] = Array.from({ length: ATTENDEE_ROW_COUNT }, () => ({
  name: "",
  role: "",
}));

/**
 * The chair's row. The web client has no access to the Technical Delegate's **formal** name —
 * the documents compose it from `HearingDocumentConstants.technicalDelegate`, which is server
 * configuration, and the only name this origin holds is the session's `appShortName` ("Annie"),
 * which is a masthead greeting and not a name to print in a served disciplinary document. So
 * the row is seeded with its role and an empty name box for the chair to type. If he leaves it
 * empty the row is dropped, and the letter still identifies him: §12 signs it with the TD
 * signature block. An omission is recoverable; a wrong name on a served document is not.
 */
const TD_CHAIR_ROLE = "Technical Delegate";

/**
 * A charged person's case role in {@link ATTENDEE_ROLES}' spelling. `HearingPersonRole` has
 * three values and two of them are already dropdown entries; `Coach` is the third, and the
 * dropdown spells it `Team Coach` after the 2026-09-24 amendment. Mapping here rather than
 * widening the vocabulary keeps one spelling per role in the filed document.
 */
function attendeeRoleForPerson(role: string): string {
  return role === "Coach" ? "Team Coach" : role;
}

/**
 * §11's attendee rows, **seeded once with the cast the case record expects** (2026-09-24).
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY THE ROWS ARRIVE FILLED IN
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Until today this block showed a read-only list of the automatic rows — manager, chair,
 * persons involved — above six empty boxes, and the generator printed that list AND then the
 * six boxes. The TD chaired two real hearings on 24 September 2026 and reported the result:
 * *"when I enter the 4 people in the attendance section, people will appear twice… when I
 * create the draft, it will always show 7-8 people by default"*. `presentBlock` now prints the
 * recorded attendees alone, which fixes the duplication; this seeding is the other half, and
 * without it the fix would merely move the work — the TD would face six empty boxes and have
 * to retype the manager and the player he can already see two fields further up the form.
 *
 * So the automatic cast becomes the **starting content of ordinary rows**: editable, and
 * removable by clearing the name, because a row with no name is not an attendee. That is the
 * point. The hearing may have been attended by an Assistant Team Manager rather than the
 * manager named on the case; the charged player may not have come; a different official may
 * have chaired. The TD corrects the pre-fill instead of fighting a list he cannot touch.
 *
 * **No Minutes Secretary row is seeded, and no ATD row.** §8's card 03 says the minutes are
 * taken by *"an assistant — an ATD or a TO"*, but that assistant is not a concept this form
 * or `HearingCaseDetailView` holds: no ATD is attached to a hearing case, only the
 * `Assistant Technical Delegate` entry in the role dropdown. Seeding a row for a person whose
 * name and very existence are unknown would put a blank line in the record and, worse, would
 * invite a radio button to be marked against nobody. The secretary is the one row the TD
 * genuinely must type, and the hint says so.
 *
 * Seeding runs **once, in the lazy initialiser**, so nothing a TD types is ever stomped by a
 * re-render. It is also written to seed only into rows that are still empty in the sense that
 * matters: this form mounts with no saved attendees — a filed minutes record reopens as a
 * read-only document, not as this form — and the guard below keeps that true if that ever
 * changes.
 */
function seedAttendeeRows(hearing: HearingCaseDetailView): AttendeeRow[] {
  const seeded: AttendeeRow[] = [];

  if (hearing.teamManagerName !== null) {
    seeded.push({ name: hearing.teamManagerName, role: "Team Manager" });
  }

  for (const person of hearing.personsInvolved) {
    // The shirt number is NOT folded into the role. It belongs to the person, both documents
    // already carry it under `REGARDING:` and in the testimony opener, and the role column is
    // now a fixed vocabulary the TD picks from — `Player No 7` would be a value no other row
    // could ever be set to by hand.
    seeded.push({ name: person.name, role: attendeeRoleForPerson(person.role) });
  }

  seeded.push({ name: "", role: TD_CHAIR_ROLE });

  return [...seeded, ...EMPTY_ROWS].slice(0, ATTENDEE_ROW_COUNT);
}

/**
 * The dropdown for one row: §11's list, plus this row's own value when the seeding composed
 * one that is not on it (`Team Manager, KOR`, `Player No 7`, `Technical Delegate (Chair)`).
 * A `<select>` whose current value is absent from its options silently shows the wrong thing,
 * and these composed roles are exactly what the document prints.
 */
function roleOptions(role: string): readonly string[] {
  return role !== "" && !ATTENDEE_ROLES.includes(role) ? [role, ...ATTENDEE_ROLES] : ATTENDEE_ROLES;
}

/** A labelled block with §11's prefill tag beside the label. */
function Labelled({
  label,
  tag,
  children,
}: {
  label: string;
  tag?: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="field minfield">
      <span className="field__label">
        {label}
        {tag === undefined ? null : <span className="minfield__tag">{tag}</span>}
      </span>
      {children}
    </div>
  );
}

/** §11's segmented controls. `aria-pressed` carries the state; colour never carries it alone. */
function Segmented<T extends string | number | boolean>({
  label,
  value,
  options,
  onPick,
  testId,
}: {
  label: string;
  value: T;
  options: readonly { value: T; label: string }[];
  onPick: (next: T) => void;
  testId: string;
}): React.JSX.Element {
  return (
    <div className="segrow" data-testid={testId}>
      <span className="segrow__label">{label}</span>
      <div className="tagpal__chips" role="group" aria-label={label}>
        {options.map((option) => (
          <button
            key={String(option.value)}
            type="button"
            className={`tag${option.value === value ? " tag--on" : ""}`}
            aria-pressed={option.value === value}
            onClick={() => {
              onPick(option.value);
            }}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  );
}

export interface MinutesFormProps {
  hearing: HearingCaseDetailView;
  onSubmit: (input: HearingMinutesRequest) => void;
  onClose: () => void;
  busy: boolean;
}

export function MinutesForm({
  hearing,
  onSubmit,
  onClose,
  busy,
}: MinutesFormProps): React.JSX.Element {
  const level = hearing.level ?? 1;
  const eventName = letterheadEventName(useTournament());

  const [heldFrom, setHeldFrom] = useState("");
  const [heldTo, setHeldTo] = useState("");
  const [matchSetting, setMatchSetting] = useState(
    hearing.matchRef === null ? "No match — the incident was off the pitch" : hearing.matchRef,
  );
  const [testimony, setTestimony] = useState("");
  const [evidenceProduced, setEvidenceProduced] = useState("");
  // Seeded ONCE, by the lazy initialiser, and only into a form that holds no recorded
  // attendees — a filed minutes record reopens as a read-only document rather than as this
  // form, so there is nothing of the TD's to stomp, and this stays true if that ever changes.
  const [rows, setRows] = useState<AttendeeRow[]>(() => seedAttendeeRows(hearing));
  const [secretaryRow, setSecretaryRow] = useState<number | null>(null);
  const [finding, setFinding] = useState("");
  const [reasoning, setReasoning] = useState("");

  // §11: `Refer Upward` defaults to `Report To FIH` when the case level is 2 or 3.
  const [picks, setPicks] = useState<DecisionPicks>({
    breachFound: true,
    seriousness: (hearing.charges[0]?.seriousness ?? "Medium") as Seriousness,
    sanction: "reprimand",
    matchesSuspended: 1,
    conditionalEscalation: false,
    referUpward: level >= 2,
    severityNarrative: "",
    acceptsBreach: true,
    attitude: "Good",
    disciplinaryHistory: "",
    penaltyAlreadyIncurred: "",
  });

  const set = <K extends keyof DecisionPicks>(key: K, value: DecisionPicks[K]): void => {
    setPicks((current) => ({ ...current, [key]: value }));
  };

  /** §11's testimony auto-opener, one sentence per person, from the shared composer. */
  const openers = useMemo(
    () =>
      hearing.personsInvolved
        .map((person) =>
          composeTestimonyOpener(
            {
              name: person.name,
              role: person.role,
              shirtNumber: person.shirtNumber,
              gender: person.gender === "f" ? "f" : "m",
            },
            hearing.charges.map(asDocumentCharge),
          ),
        )
        .join("\n"),
    [hearing.personsInvolved, hearing.charges],
  );

  const secretaryName = secretaryRow === null ? null : (rows[secretaryRow]?.name ?? null);
  const signature = minutesSignatureLine(secretaryName, formatDay(hearing.hearingAt));
  const composed = composeDecisionPreview(hearing, picks);

  const submit = (): void => {
    const attendees = rows
      .map((row, index) => ({
        name: row.name.trim(),
        role: row.role === "" ? "Other" : row.role,
        isMinutesSecretary: index === secretaryRow,
      }))
      .filter((row) => row.name !== "");

    onSubmit({
      heldFrom: heldFrom.trim() === "" ? null : heldFrom.trim(),
      heldTo: heldTo.trim() === "" ? null : heldTo.trim(),
      matchSetting: matchSetting.trim() === "" ? null : matchSetting.trim(),
      // The composed opener is the first sentence of the record; §11 says everything after it
      // is the secretary's, so both go, in that order.
      testimony: [openers, testimony.trim()].filter((part) => part !== "").join("\n"),
      evidenceProduced: evidenceProduced.trim(),
      attendees,
      decision: {
        breachFound: picks.breachFound,
        acceptsBreach: picks.acceptsBreach,
        finding: finding.trim(),
        reasoning: reasoning.trim(),
        seriousness: picks.seriousness,
        attitude: picks.attitude,
        disciplinaryHistory:
          picks.disciplinaryHistory.trim() === "" ? null : picks.disciplinaryHistory.trim(),
        penaltyAlreadyIncurred:
          picks.penaltyAlreadyIncurred.trim() === "" ? null : picks.penaltyAlreadyIncurred.trim(),
        sanction: picks.sanction,
        matchesSuspended: picks.sanction === "suspension" ? picks.matchesSuspended : null,
        conditionalEscalation: picks.conditionalEscalation,
        referUpward: picks.referUpward,
        severityNarrative:
          picks.severityNarrative.trim() === "" ? null : picks.severityNarrative.trim(),
      },
    });
  };

  return (
    <section className="docwork" aria-label={workspaceTitle(2, "pending")} data-testid="workspace">
      <div className="docwork__bar">
        <b className="docwork__title" data-testid="workspaceTitle">
          {workspaceTitle(2, "pending")}
        </b>
        <span className="statuspill" data-testid="workspaceChip">
          <span className="sdot" aria-hidden="true" />
          {HEARING_MINUTES_WORKSPACE_LABELS.pending}
        </span>
        <span className="spacer" />
        <button type="button" className="gbtn" onClick={onClose}>
          Close
        </button>
      </div>

      <div className="warnnote warnnote--attention" role="note" data-testid="prefillBanner">
        <span className="warnnote__kind">Pre-Filled</span>
        <span className="warnnote__msg">{MINUTES_PREFILL_BANNER}</span>
      </div>

      <DocumentPaper>
        <div className="minform">
          <Labelled label="Header" tag="From Tournament Configuration">
            {/* The event comes from the tournament record, exactly as the letterhead above
                it does. An installation with no tournament set up names none — the tag on
                this field already says where the name is configured. */}
            <p className="minread" data-testid="minutesHeader">
              {eventName === null ? null : `${eventName} · `}Code Of Conduct Hearing · Minutes
            </p>
          </Labelled>

          <Labelled label="Team Manager" tag="Pre-Populated">
            <p className="minread" data-testid="minutesManager">
              {hearing.teamCode ?? "—"} —{" "}
              {hearing.teamManagerName ?? "No Team Manager is named on this case"}
            </p>
          </Labelled>

          <Labelled label="Persons Involved" tag="Pre-Populated">
            <ul className="minread minread--list" data-testid="minutesPersons">
              {hearing.personsInvolved.map((person) => (
                <li key={person.name}>
                  {person.name} — {person.role}
                  {person.shirtNumber === null ? "" : `, No ${String(person.shirtNumber)}`}
                </li>
              ))}
            </ul>
          </Labelled>

          <Labelled label="Match Involved / Setting" tag="Pre-Populated · Editable">
            <input
              type="text"
              value={matchSetting}
              aria-label="Match Involved / Setting"
              onChange={(event) => {
                setMatchSetting(event.target.value);
              }}
            />
          </Labelled>

          <Labelled label="Charge" tag="Pre-Populated · All Charges">
            <ul className="minread minread--list" data-testid="minutesCharges">
              {hearing.charges.map((charge) => (
                <li key={charge.article}>
                  Level {charge.level}, Article {charge.article} — &ldquo;{charge.articleText}
                  &rdquo;
                </li>
              ))}
            </ul>
          </Labelled>

          <Labelled label="Date And Time Of The Hearing" tag="Location And Date Pre-Populated">
            <p className="minread" data-testid="minutesWhen">
              {hearing.hearingLocation ?? "Technical Delegate's Office"} ·{" "}
              {formatInstant(hearing.hearingAt) ?? "Hearing time not set"}
            </p>
            <div className="stamprow">
              <label className="field field--inline">
                <span className="field__sub">From, for example 14:00</span>
                <input
                  type="text"
                  value={heldFrom}
                  onChange={(event) => {
                    setHeldFrom(event.target.value);
                  }}
                />
              </label>
              <label className="field field--inline">
                <span className="field__sub">To, for example 14:41</span>
                <input
                  type="text"
                  value={heldTo}
                  onChange={(event) => {
                    setHeldTo(event.target.value);
                  }}
                />
              </label>
            </div>
          </Labelled>

          {/* No read-only list of automatic rows sits above these boxes any more. It was the
              visible half of the duplication the TD reported on 2026-09-24: the documents
              printed that list AND everything typed below it. The same cast is now the
              starting content of the rows themselves, where it can be corrected. */}
          <Labelled label="Attended By" tag="Pre-Filled From The Case · Editable">
            <div className="attrows" data-testid="attendeeRows">
              {rows.map((row, index) => (
                // The index is a stable key here and only here: §11 gives six FIXED rows that
                // are never added to, removed or reordered — the row is the identity.
                <div className="attrow" key={index}>
                  <input
                    type="text"
                    placeholder="Name"
                    aria-label={`Attendee ${String(index + 1)} name`}
                    value={row.name}
                    onChange={(event) => {
                      const next = event.target.value;
                      setRows((current) =>
                        current.map((item, at) => (at === index ? { ...item, name: next } : item)),
                      );
                    }}
                  />
                  <select
                    aria-label={`Attendee ${String(index + 1)} role`}
                    value={row.role}
                    onChange={(event) => {
                      const next = event.target.value;
                      setRows((current) =>
                        current.map((item, at) => (at === index ? { ...item, role: next } : item)),
                      );
                    }}
                  >
                    <option value="">Role…</option>
                    {roleOptions(row.role).map((role) => (
                      <option key={role} value={role}>
                        {role}
                      </option>
                    ))}
                  </select>
                  <label className="attrow__sec">
                    <input
                      type="radio"
                      name="minutesSecretary"
                      checked={secretaryRow === index}
                      aria-label={`Mark attendee ${String(index + 1)} as Minutes Secretary`}
                      onChange={() => {
                        setSecretaryRow(index);
                      }}
                    />
                    Minutes Secretary
                  </label>
                </div>
              ))}
            </div>
            <span className="field__sub">{ATTENDEE_HINT}</span>
          </Labelled>

          <Labelled label="Incident" tag="Carried From The Hearing Letter">
            <p className="minread" data-testid="minutesIncident">
              {hearing.incidentDetails}
            </p>
          </Labelled>

          <Labelled label="Testimony" tag="Opening Sentence Auto-Composed">
            <p className="minread" data-testid="testimonyOpener">
              {openers}
            </p>
            <textarea
              aria-label="Testimony"
              rows={5}
              value={testimony}
              onChange={(event) => {
                setTestimony(event.target.value);
              }}
            />
            <span className="field__sub">{TESTIMONY_HINT}</span>
          </Labelled>

          <Labelled label="Evidence Produced" tag="From The Evidence Files">
            <textarea
              aria-label="Evidence Produced"
              rows={3}
              value={evidenceProduced}
              onChange={(event) => {
                setEvidenceProduced(event.target.value);
              }}
            />
            <span className="field__sub">
              One item per line. Left empty, the record reads &ldquo;Written report of the
              reporting official&rdquo;.
            </span>
          </Labelled>

          <Labelled label="Disciplinary History">
            <input
              type="text"
              aria-label="Disciplinary History"
              placeholder="Cards or previous cases at this tournament"
              value={picks.disciplinaryHistory}
              onChange={(event) => {
                set("disciplinaryHistory", event.target.value);
              }}
            />
          </Labelled>

          <Segmented<HearingAttitude>
            label="Attitude At The Hearing"
            testId="attitudeRow"
            value={picks.attitude}
            options={[
              { value: "Not Good", label: "Not Good" },
              { value: "Good", label: "Good" },
              { value: "Very Good", label: "Very Good" },
            ]}
            onPick={(next) => {
              set("attitude", next);
            }}
          />

          <Labelled label="Penalty Already Incurred">
            <input
              type="text"
              aria-label="Penalty Already Incurred"
              placeholder="For example five minutes served under a yellow card"
              value={picks.penaltyAlreadyIncurred}
              onChange={(event) => {
                set("penaltyAlreadyIncurred", event.target.value);
              }}
            />
          </Labelled>

          <Labelled label="Decision" tag="Composed From Your Picks">
            <div className="decbuild" data-testid="decisionBuilder">
              <Segmented<boolean>
                label="Breach Found"
                testId="breachRow"
                value={picks.breachFound}
                options={[
                  { value: true, label: "Yes — Breach Found" },
                  { value: false, label: "No Breach" },
                ]}
                onPick={(next) => {
                  set("breachFound", next);
                }}
              />
              <Segmented<boolean>
                label="Accepts The Breach"
                testId="acceptsRow"
                value={picks.acceptsBreach}
                options={[
                  { value: true, label: "Yes — The Breach Was Accepted" },
                  { value: false, label: "No — A Finding Was Required" },
                ]}
                onPick={(next) => {
                  set("acceptsBreach", next);
                }}
              />
              <Segmented<Seriousness>
                label={`Seriousness Within The Level`}
                testId="seriousnessRow"
                value={picks.seriousness}
                options={[
                  { value: "Low", label: "Low" },
                  { value: "Medium", label: "Medium" },
                  { value: "High", label: "High" },
                ]}
                onPick={(next) => {
                  set("seriousness", next);
                }}
              />
              <Segmented<HearingSanction>
                label="Sanction"
                testId="sanctionRow"
                value={picks.sanction}
                options={[
                  { value: "no_action", label: "No Further Action" },
                  { value: "reprimand", label: "Official Reprimand" },
                  { value: "suspension", label: "Suspension" },
                ]}
                onPick={(next) => {
                  set("sanction", next);
                }}
              />
              <div className="segrow">
                <span className="segrow__label">Matches Suspended</span>
                <span className="stamprow">
                  <input
                    type="number"
                    min={1}
                    max={10}
                    aria-label="Matches suspended"
                    disabled={picks.sanction !== "suspension"}
                    value={picks.matchesSuspended}
                    onChange={(event) => {
                      set("matchesSuspended", Number.parseInt(event.target.value, 10) || 1);
                    }}
                  />
                  <span className="field__sub">matches</span>
                </span>
              </div>
              <Segmented<boolean>
                label="Conditional Escalation · reprimands"
                testId="escalationRow"
                value={picks.conditionalEscalation}
                options={[
                  { value: true, label: "Include Sentence" },
                  { value: false, label: "Omit" },
                ]}
                onPick={(next) => {
                  set("conditionalEscalation", next);
                }}
              />
              <Segmented<boolean>
                label="Refer Upward · Level 2 and 3"
                testId="referRow"
                value={picks.referUpward}
                options={[
                  { value: true, label: "Report To FIH" },
                  { value: false, label: "Not Required" },
                ]}
                onPick={(next) => {
                  set("referUpward", next);
                }}
              />
              <Labelled label="Severity Narrative · optional">
                <input
                  type="text"
                  aria-label="Severity Narrative"
                  placeholder="For example: The action was reckless and endangered an opponent."
                  value={picks.severityNarrative}
                  onChange={(event) => {
                    set("severityNarrative", event.target.value);
                  }}
                />
              </Labelled>
            </div>

            <DocumentView body={composed} testId="composedDecision" />
            <span className="field__sub">{COMPOSED_DECISION_HINT}</span>
          </Labelled>

          <Labelled label="Finding">
            <textarea
              aria-label="Finding"
              rows={3}
              value={finding}
              onChange={(event) => {
                setFinding(event.target.value);
              }}
            />
            <span className="field__sub">
              Your finding on the case. It is printed under its own heading in the Decision
              Letter.
            </span>
          </Labelled>

          <Labelled label="Reasoning">
            <textarea
              aria-label="Reasoning"
              rows={4}
              value={reasoning}
              onChange={(event) => {
                setReasoning(event.target.value);
              }}
            />
            <span className="field__sub">
              The narrative behind the penalty — history, attitude, any penalty already
              incurred and the seriousness relative to other offences at this level. Printed
              under the Decision Letter&apos;s REASONING heading.
            </span>
          </Labelled>

          <Labelled label="Signature" tag="From The Minutes Secretary Row">
            <p className="minread minread--sig" data-testid="signatureLine">
              {signature}
            </p>
          </Labelled>
        </div>
      </DocumentPaper>

      <div className="docwork__acts">
        <button
          type="button"
          className="gbtn gbtn--solid"
          disabled={busy}
          onClick={submit}
          data-testid="workspacePrimary"
        >
          {busy ? "Working…" : WORKSPACE_PRIMARY[2]}
        </button>
        <span className="btnhint" data-testid="workspaceCaption">
          {WORKSPACE_PRIMARY_CAPTION[2]}
        </span>
      </div>
    </section>
  );
}
